import { useEffect, useState } from 'react';
import { getProGames } from './espnReader';
import { isAnyGameLive, msUntilNextKickoff } from './util';

const LIVE_POLL_MS = 30 * 1000;
const IDLE_RECHECK_MS = 60 * 60 * 1000;

// Drives live score updates. While any NFL game is in progress, `refreshKey`
// increments every 30s; components put it in their fetch effect's deps to
// refetch. Between games it sleeps until the next kickoff, and it pauses
// entirely while the tab is hidden (catching up as soon as it's visible).
// `proGames` is the NFL schedule it polls, shared for players' game states.
export default function useLiveRefresh(season) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLive, setIsLive] = useState(false);
  const [proGames, setProGames] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let checking = false;
    let timer;
    // The components do their own initial fetch, so the first check only
    // works out whether to keep polling.
    let firstCheck = true;

    const check = async () => {
      clearTimeout(timer);
      if (document.hidden || checking) return;
      checking = true;
      let games = [];
      try {
        games = await getProGames(season);
      } catch (error) {
        // Treat a failed schedule fetch as "nothing live"; the idle recheck
        // will try again.
      }
      checking = false;
      if (cancelled) return;

      const now = Date.now();
      const live = isAnyGameLive(games, now);
      if (games.length > 0) setProGames(games);
      setIsLive(live);
      if (live && !firstCheck) setRefreshKey(key => key + 1);
      firstCheck = false;
      timer = setTimeout(check, live ? LIVE_POLL_MS : msUntilNextKickoff(games, now, IDLE_RECHECK_MS));
    };

    const onVisibilityChange = () => {
      if (!document.hidden) check();
    };

    check();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [season]);

  return { refreshKey, isLive, proGames };
}
